/**
 * CurveBase - Abstract base class for all curve types
 *
 * Curves are 1D geometry: lines, polylines, arcs, circles, NURBS curves.
 */

import { GeometryBase } from '../base/GeometryBase';
import { Vector } from '../base/Vector';

export class CurveBase extends GeometryBase {
  constructor(type, idPrefix = 'crv', attributes = {}) {
    super(type, idPrefix, attributes);
  }

  /**
   * Get curve length
   * @returns {number} Length in mm
   */
  get length() {
    throw new Error('length must be implemented by subclass');
  }

  /**
   * Check if curve is closed
   * @returns {boolean}
   */
  get isClosed() {
    return false;
  }

  /**
   * Get point at parameter t (0-1)
   * @param {number} t - Parameter from 0 (start) to 1 (end)
   * @returns {Vector} Point on curve
   */
  pointAt(t) {
    throw new Error('pointAt() must be implemented by subclass');
  }

  /**
   * Get tangent at parameter t
   * @param {number} t - Parameter from 0 to 1
   * @returns {Vector} Tangent vector (normalized)
   */
  tangentAt(t) {
    throw new Error('tangentAt() must be implemented by subclass');
  }

  /**
   * Get start point
   * @returns {Vector}
   */
  get startPoint() {
    return this.pointAt(0);
  }

  /**
   * Get end point
   * @returns {Vector}
   */
  get endPoint() {
    return this.pointAt(1);
  }

  /**
   * Get midpoint
   * @returns {Vector}
   */
  get midPoint() {
    return this.pointAt(0.5);
  }

  /**
   * Find closest point on curve to given point
   * @param {Vector} point
   * @returns {{point: Vector, parameter: number, distance: number}}
   */
  closestPoint(point) {
    // Default implementation: sample curve
    const samples = 100;
    let minDist = Infinity;
    let closestT = 0;
    let closestPt = null;

    for (let i = 0; i <= samples; i++) {
      const t = i / samples;
      const pt = this.pointAt(t);
      const dist = pt.distanceTo(point);
      if (dist < minDist) {
        minDist = dist;
        closestT = t;
        closestPt = pt;
      }
    }

    // Refine with binary search
    let step = 1 / (samples * 2);
    for (let i = 0; i < 10; i++) {
      const tMinus = Math.max(0, closestT - step);
      const tPlus = Math.min(1, closestT + step);
      const ptMinus = this.pointAt(tMinus);
      const ptPlus = this.pointAt(tPlus);
      const distMinus = ptMinus.distanceTo(point);
      const distPlus = ptPlus.distanceTo(point);

      if (distMinus < minDist) {
        minDist = distMinus;
        closestT = tMinus;
        closestPt = ptMinus;
      } else if (distPlus < minDist) {
        minDist = distPlus;
        closestT = tPlus;
        closestPt = ptPlus;
      }
      step /= 2;
    }

    return {
      point: closestPt,
      parameter: closestT,
      distance: minDist,
    };
  }

  /**
   * Split curve at parameter
   * @param {number} t - Split parameter
   * @returns {[CurveBase, CurveBase]} Two curve segments
   */
  splitAt(t) {
    throw new Error('splitAt() must be implemented by subclass');
  }

  /**
   * Get points for display (polyline approximation)
   * @param {number} segments - Number of segments
   * @returns {Vector[]} Array of points
   */
  getDisplayPoints(segments = 32) {
    const points = [];
    for (let i = 0; i <= segments; i++) {
      points.push(this.pointAt(i / segments));
    }
    return points;
  }

  /**
   * Get Three.js compatible positions array
   * @param {number} segments
   * @returns {Float32Array}
   */
  getPositionsArray(segments = 32) {
    const points = this.getDisplayPoints(segments);
    const positions = new Float32Array(points.length * 3);
    points.forEach((p, i) => {
      positions[i * 3] = p.x;
      positions[i * 3 + 1] = p.y;
      positions[i * 3 + 2] = p.z;
    });
    return positions;
  }
}

export default CurveBase;
